import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
});

// =====================================================
// REQUEST INTERCEPTOR
// Automatically attach access token
// =====================================================

api.interceptors.request.use(
  (config) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      if (user?.access) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${user.access}`;
      }
    } catch (error) {
      console.error("Error reading user from localStorage:", error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


// =====================================================
// REFRESH STATE
// =====================================================

let isRefreshing = false;
let failedQueue = [];


// =====================================================
// PROCESS QUEUED REQUESTS
// =====================================================

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};


// =====================================================
// RESPONSE INTERCEPTOR
// =====================================================

api.interceptors.response.use(
  (response) => {
    return response;
  },

  async (error) => {
    const originalRequest = error.config;

    // No response from server
    if (!error.response) {
      return Promise.reject(error);
    }

    // Only handle 401
    if (error.response.status !== 401) {
      return Promise.reject(error);
    }

    // =================================================
    // GET USER
    // =================================================

    let user = {};

    try {
      user = JSON.parse(localStorage.getItem("user") || "{}");
    } catch (parseError) {
      console.error("Could not parse user data:", parseError);

      localStorage.removeItem("user");

      return Promise.reject(error);
    }

    // =================================================
    // NO REFRESH TOKEN
    // =================================================

    if (!user?.refresh) {
      console.log("No refresh token available.");

      return Promise.reject(error);
    }

    // =================================================
    // PREVENT INFINITE RETRY
    // =================================================

    if (originalRequest?._retry) {
      return Promise.reject(error);
    }

    // =================================================
    // WAIT IF REFRESH IS ALREADY RUNNING
    // =================================================

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve,
          reject,
        });
      }).then((newAccessToken) => {
        originalRequest.headers = originalRequest.headers || {};

        originalRequest.headers.Authorization =
          `Bearer ${newAccessToken}`;

        return api(originalRequest);
      });
    }

    // =================================================
    // START REFRESH
    // =================================================

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      console.log("=================================");
      console.log("ACCESS TOKEN EXPIRED");
      console.log("STARTING TOKEN REFRESH");
      console.log("=================================");

      // IMPORTANT:
      // Use plain axios, not "api".
      // This prevents the refresh request from
      // triggering this interceptor again.

      const refreshResponse = await axios.post(
        `${API_URL}/api/token/refresh/`,
        {
          refresh: user.refresh,
        }
      );

      console.log("Refresh response:", refreshResponse.data);

      const newAccessToken = refreshResponse.data?.access;

      // =================================================
      // MAKE SURE NEW ACCESS TOKEN EXISTS
      // =================================================

      if (!newAccessToken) {
        throw new Error(
          "Refresh endpoint did not return an access token."
        );
      }

      console.log("ACCESS TOKEN REFRESHED SUCCESSFULLY");

      // =================================================
      // SAVE NEW ACCESS TOKEN
      // =================================================

      const updatedUser = {
        ...user,
        access: newAccessToken,
      };

      localStorage.setItem(
        "user",
        JSON.stringify(updatedUser)
      );

      // =================================================
      // RELEASE WAITING REQUESTS
      // =================================================

      processQueue(null, newAccessToken);

      // =================================================
      // RETRY ORIGINAL REQUEST
      // =================================================

      originalRequest.headers = originalRequest.headers || {};

      originalRequest.headers.Authorization =
        `Bearer ${newAccessToken}`;

      return api(originalRequest);

    } catch (refreshError) {
      console.error(
        "================================="
      );

      console.error(
        "TOKEN REFRESH FAILED"
      );

      console.error(
        refreshError?.response?.data || refreshError
      );

      console.error(
        "================================="
      );

      // Reject queued requests
      processQueue(refreshError, null);

      // Refresh token is invalid/expired
      localStorage.removeItem("user");

      // DO NOT redirect anywhere
      return Promise.reject(refreshError);

    } finally {
      isRefreshing = false;
    }
  }
);

export default api;